import { AxiosRequestConfig } from 'axios'
import { ApiError, ApiErrorTypes, ApiRest } from 'ogram-react'

import { store } from '../../app'
import { captureError } from '../sentry'

const TOKEN_LOGIN_URL = 'client/token'
const MAX_LOGIN_ATTEMPTS = 3

// A token the backend turned down stays turned down, so retrying it only adds failing requests.
const PERMANENT_ERROR_TYPES: string[] = [
  ApiErrorTypes.AuthError,
  ApiErrorTypes.ClientError,
  ApiErrorTypes.NotFound,
  ApiErrorTypes.PermissionError,
]

let loggedInByToken = false
let tokenRejected = false
let attemptsMade = 0
let loginRequest: Promise<void> | null = null

export async function secretTokenInterceptor(this: ApiRest, request: AxiosRequestConfig) {
  const urlParams = new URLSearchParams(window.location.search)
  const token = urlParams.get('token')

  const shouldLogIn =
    Boolean(token) &&
    !loggedInByToken &&
    !tokenRejected &&
    attemptsMade < MAX_LOGIN_ATTEMPTS &&
    request.url !== TOKEN_LOGIN_URL

  if (shouldLogIn) {
    let pendingLogin = loginRequest
    // Requests fired before the log in resolves share its result instead of each spending the token.
    const startsAttempt = pendingLogin === null

    if (pendingLogin === null) {
      attemptsMade += 1
      pendingLogin = store.data.logInByToken({ token: String(token) })
      loginRequest = pendingLogin
    }

    try {
      await pendingLogin
      loggedInByToken = true
    } catch (error) {
      // Let the next request start a new attempt, in case the failure was a transient one.
      loginRequest = null
      // Only the request that started the attempt reports it, so one failure is one report.
      if (startsAttempt) {
        reportTokenLoginFailure(error)
      }
    }
  }

  return request
}

/**
 * A failed token log in leaves the app rendering pages while its store still holds no account,
 * which is silent to the user, so it has to be loud in Sentry.
 * @param error
 * @returns void
 */
function reportTokenLoginFailure(error: unknown) {
  const apiError = error instanceof ApiError ? error : null
  tokenRejected = apiError !== null && PERMANENT_ERROR_TYPES.includes(apiError.type)

  captureError('Token link login failed', {
    // window.location.search is left out on purpose - it carries the token, which is a credential.
    path: window.location.pathname,
    errorType: apiError?.type ?? (error instanceof Error ? error.name : typeof error),
    errorCode: apiError?.data.code,
    errorMessage: apiError?.data.message ?? String(error),
    attempt: attemptsMade,
    willRetry: !tokenRejected && attemptsMade < MAX_LOGIN_ATTEMPTS,
  })
}
